import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { Schemas } from '@crm/contracts';
import { unwrap } from '../api/client.ts';
import { qk } from '../api/queryKeys.ts';
import { useTenant } from '../tenant/TenantProvider.tsx';
import { addCalendarDays, mondayOf } from './week.ts';

export type Visit = Schemas['Visit'];
export type VisitSummary = Schemas['VisitSummary'];
export type VisitSensitive = Schemas['VisitSensitive'];
export type SensitiveVisit = Visit & { sensitive: VisitSensitive };
export type VisitDefinition = Schemas['VisitDefinition'];
export type VisitDefinitionRef = Schemas['VisitDefinitionRef'];
export type VisitFollowUp = Schemas['VisitFollowUp'];
export type VisitAccount = Schemas['VisitAccountLookupRef'];
export type VisitMember = Schemas['VisitMemberLookupRef'];
export type VisitEntity = Schemas['VisitEntityLookupRef'];
export type VisitPlanningWeek = Schemas['VisitPlanningWeek'];
export type VisitTour = Schemas['VisitTour'];
export type VisitTourRevision = Schemas['VisitTourRevision'];
export type VisitTourStop = Schemas['VisitTourStop'];
export type VisitMapItem = Schemas['VisitMapItem'];
export type VisitMapResponse = Schemas['VisitMap'];
export type VisitDashboardResponse = Schemas['VisitDashboard'];
export type VisitAlert = Schemas['VisitAlert'];
export type VisitAlertList = Schemas['VisitAlertPage'];
export type VisitOperationalSummary = Schemas['VisitOperationalSummary'];

export const PHOTO_URL_REFRESH_MS = 10 * 60_000;

export function useVisit(id: string) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visit(tenantId, id),
    queryFn: () => unwrap(api.GET('/visits/{id}', { params: { path: { id }, header: { 'X-Tenant-Id': tenantId } } })),
    refetchInterval: PHOTO_URL_REFRESH_MS,
  });
}

export function useVisitAccounts(q: string, ids?: string, enabled = true, entityId?: string) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitLookup(tenantId, 'accounts', { q, ids, entityId }),
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await unwrap(api.GET('/visits/lookups/accounts', { params: { header: { 'X-Tenant-Id': tenantId }, query: { q: q || undefined, ids, entity_id: entityId, limit: 50 } } }))).items,
  });
}

export function useVisitEntities(enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitLookup(tenantId, 'entities'),
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await unwrap(api.GET('/visits/lookups/entities', { params: { header: { 'X-Tenant-Id': tenantId }, query: { limit: 200 } } }))).items,
  });
}

export function useVisitWeek(entityId: string, weekStart: string, agentId?: string, enabled = true) {
  const { api, tenantId } = useTenant();
  const filters = { entityId, weekStart, agentId: agentId ?? '' };
  return useQuery({
    queryKey: qk.visitWeek(tenantId, filters),
    enabled: enabled && !!entityId && !!weekStart,
    queryFn: () => unwrap(api.GET('/visits/planning/week', {
      params: { header: { 'X-Tenant-Id': tenantId }, query: { entity_id: entityId, week_start: weekStart, agent_membership_id: agentId || undefined } },
    })),
  });
}

export function useVisitCalendar(entityId: string, from: string, to: string, agentId?: string, enabled = true) {
  const { api, tenantId } = useTenant();
  const weeks: string[] = [];
  if (from && to) {
    for (let cursor = mondayOf(from); cursor <= to; cursor = addCalendarDays(cursor, 7)) weeks.push(cursor);
  }
  const filters = { entityId, from, to, agentId: agentId ?? '' };
  return useQuery({
    queryKey: qk.visitWeek(tenantId, filters),
    enabled: enabled && !!entityId && !!from && !!to,
    queryFn: async () => {
      const pages = await Promise.all(weeks.map((weekStart) => unwrap(api.GET('/visits/planning/week', {
        params: { header: { 'X-Tenant-Id': tenantId }, query: { entity_id: entityId, week_start: weekStart, agent_membership_id: agentId || undefined } },
      }))));
      const first = pages[0]!;
      return {
        entity_timezone: first.entity_timezone,
        days: pages.flatMap((page) => page.days).filter((day) => day.date >= from && day.date <= to),
        overlaps: pages.flatMap((page) => page.overlaps),
      };
    },
  });
}

export interface VisitSummaryQuery {
  entityId?: string;
  accountId?: string;
  agentId?: string;
  mine?: boolean;
  from?: string;
  to?: string;
  today?: boolean;
  localFrom?: string;
  localTo?: string;
}

export function useVisitOperationalSummary(filters: VisitSummaryQuery) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visits(tenantId, { summary: true, ...filters }),
    queryFn: () => unwrap(api.GET('/visits/summary', { params: { header: { 'X-Tenant-Id': tenantId }, query: {
      entity_id: filters.entityId,
      account_id: filters.accountId,
      agent_membership_id: filters.agentId,
      mine: filters.mine || undefined,
      from: filters.from,
      to: filters.to,
      today: filters.today || undefined,
      local_from: filters.localFrom,
      local_to: filters.localTo,
    } } })),
  });
}

export interface VisitMapQuery {
  entityIds: string[];
  from?: string;
  to?: string;
  agentId?: string;
  status?: Schemas['VisitStatus'];
  bounds?: string;
}

export function useVisitMap(filters: VisitMapQuery, enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitMap(tenantId, filters),
    enabled: enabled && filters.entityIds.length > 0,
    queryFn: () => Promise.all(filters.entityIds.map(async (entityId) => {
      const pages: VisitMapResponse[] = [];
      const seenCursors = new Set<string>();
      let cursor: string | undefined;
      do {
        const page = await unwrap(api.GET('/visits/map', {
          params: {
            header: { 'X-Tenant-Id': tenantId },
            query: {
              entity_id: entityId,
              from: filters.from || undefined,
              to: filters.to || undefined,
              agent_membership_id: filters.agentId || undefined,
              status: filters.status || undefined,
              bounds: filters.bounds || undefined,
              cursor,
            },
          },
        }));
        pages.push(page);
        cursor = page.next_cursor ?? undefined;
        if (cursor && seenCursors.has(cursor)) throw new Error('Curseur cartographique répété.');
        if (cursor) seenCursors.add(cursor);
      } while (cursor);
      const first = pages[0]!;
      return { ...first, markers: pages.flatMap((page) => page.markers),
        missing_coordinates: pages.flatMap((page) => page.missing_coordinates), truncated: false, next_cursor: null };
    })),
  });
}

export interface VisitDashboardQuery {
  entityId: string;
  from: string;
  to: string;
  agentId?: string;
}

export function useVisitDashboard(filters: VisitDashboardQuery, enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitDashboard(tenantId, filters),
    enabled: enabled && !!filters.entityId && !!filters.from && !!filters.to,
    queryFn: () => unwrap(api.GET('/visits/dashboard', {
      params: {
        header: { 'X-Tenant-Id': tenantId },
        query: {
          entity_id: filters.entityId,
          from: filters.from,
          to: filters.to,
          agent_membership_id: filters.agentId || undefined,
        },
      },
    })),
  });
}

export function useVisitAlerts(entityId: string, agentId?: string, type?: Schemas['VisitAlertType'], enabled = true) {
  const { api, tenantId } = useTenant();
  const filters = { entityId, agentId: agentId ?? '', type: type ?? '' };
  return useInfiniteQuery({
    queryKey: qk.visitAlerts(tenantId, filters),
    enabled: enabled && !!entityId,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => unwrap(api.GET('/visits/alerts', {
      params: {
        header: { 'X-Tenant-Id': tenantId },
        query: {
          entity_id: entityId,
          agent_membership_id: agentId || undefined,
          type: type || undefined,
          cursor: pageParam,
          limit: 100,
        },
      },
    })),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

export function useVisitTours(entityId: string, weekStart: string, agentId?: string, enabled = true) {
  const { api, tenantId } = useTenant();
  const filters = { entityId, weekStart, agentId: agentId ?? '' };
  return useQuery({
    queryKey: qk.visitTours(tenantId, filters),
    enabled: enabled && !!entityId,
    queryFn: async () => (await unwrap(api.GET('/visit-tours', {
      params: { header: { 'X-Tenant-Id': tenantId }, query: { entity_id: entityId, week_start: weekStart || undefined, agent_membership_id: agentId || undefined, limit: 200 } },
    }))).items,
  });
}

export function useVisitTour(id: string, enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitTour(tenantId, id),
    enabled: enabled && !!id,
    queryFn: () => unwrap(api.GET('/visit-tours/{id}', { params: { path: { id }, header: { 'X-Tenant-Id': tenantId } } })),
  });
}

export function useVisitTourRevision(id: string, enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitTourRevision(tenantId, id),
    enabled: enabled && !!id,
    queryFn: () => unwrap(api.GET('/visit-tour-revisions/{id}', { params: { path: { id }, header: { 'X-Tenant-Id': tenantId } } })),
  });
}

export function useVisitMembers(q: string, options: { enabled?: boolean; accountId?: string; entityId?: string } = {}) {
  const { api, tenantId, can } = useTenant();
  return useQuery({
    queryKey: qk.visitLookup(tenantId, 'members', { q, accountId: options.accountId, entityId: options.entityId }),
    enabled: (options.enabled ?? true) && can('visits.manage'),
    staleTime: 60_000,
    queryFn: async () => (await unwrap(api.GET('/visits/lookups/members', { params: { header: { 'X-Tenant-Id': tenantId }, query: { q: q || undefined, account_id: options.accountId, entity_id: options.entityId, limit: 100 } } }))).items,
  });
}

export function useAgents(q: string, enabled = true) {
  const { api, tenantId, can } = useTenant();
  return useQuery({
    queryKey: qk.accountOwners(tenantId, q),
    enabled: enabled && can('accounts.read'),
    staleTime: 60_000,
    queryFn: async () => (await unwrap(api.GET('/account-owners', { params: { header: { 'X-Tenant-Id': tenantId }, query: { q: q || undefined } } }))).items,
  });
}

export function useVisitContacts(accountId: string, enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitLookup(tenantId, 'contacts', { accountId }),
    enabled: enabled && !!accountId,
    staleTime: 60_000,
    queryFn: async () => (await unwrap(api.GET('/visits/lookups/contacts', { params: { header: { 'X-Tenant-Id': tenantId }, query: { account_id: accountId, limit: 200 } } }))).items,
  });
}

export function usePublishedForms(entityId?: string, enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitLookup(tenantId, 'forms', { entityId }),
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await unwrap(api.GET('/visits/lookups/forms', { params: { header: { 'X-Tenant-Id': tenantId }, query: { entity_id: entityId, limit: 200 } } }))).items,
  });
}

export type VisitDefinitionKind = 'goals' | 'outcomes' | 'follow-ups';

export function useVisitDefinitionLookups(kind: VisitDefinitionKind, entityId?: string, enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitLookup(tenantId, kind, { entityId }),
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const params = { header: { 'X-Tenant-Id': tenantId }, query: { entity_id: entityId, limit: 200 } };
      if (kind === 'goals') return (await unwrap(api.GET('/visits/lookups/goals', { params }))).items;
      if (kind === 'outcomes') return (await unwrap(api.GET('/visits/lookups/outcomes', { params }))).items;
      return (await unwrap(api.GET('/visits/lookups/follow-ups', { params }))).items;
    },
  });
}

export function useVisitSettings(enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitSettings(tenantId),
    enabled,
    staleTime: 60_000,
    queryFn: () => unwrap(api.GET('/visits/settings', { params: { header: { 'X-Tenant-Id': tenantId } } })),
  });
}

export function useVisitFollowUps(visitId: string, enabled = true) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.visitFollowUps(tenantId, visitId),
    enabled,
    queryFn: async () => (await unwrap(api.GET('/visits/{id}/follow-ups', { params: { path: { id: visitId }, header: { 'X-Tenant-Id': tenantId }, query: { limit: 200 } } }))).items,
  });
}

export function useMyMembershipId(): string | null | undefined {
  return useTenant().context.membership_id;
}

export const DONE_PHOTO_GRACE_MS = 24 * 60 * 60 * 1000;
export const MAX_PHOTOS_PER_VISIT = 30;

export function visitRights(v: Visit, ctx: { can: (p: string) => boolean; myMembershipId: string | null | undefined; now?: number }) {
  const write = ctx.can('visits.write');
  const isAgent = !!ctx.myMembershipId && v.agent_membership_id === ctx.myMembershipId;
  const manage = ctx.can('visits.manage');
  const sensitive = v.sensitive !== null;
  const agentOrManager = write && sensitive && (isAgent || manage);
  const now = ctx.now ?? Date.now();
  const doneRecently = v.status === 'DONE' && !!v.check_out_at && now - Date.parse(v.check_out_at) <= DONE_PHOTO_GRACE_MS;
  const photosOpen = agentOrManager && (v.status === 'PLANNED' || v.status === 'IN_PROGRESS' || doneRecently);
  return {
    isAgent,
    edit: agentOrManager && v.status === 'PLANNED',
    delete: agentOrManager && v.status === 'PLANNED',
    cancel: agentOrManager && (v.status === 'PLANNED' || v.status === 'IN_PROGRESS'),
    checkIn: write && sensitive && isAgent && v.status === 'PLANNED',
    checkOut: write && sensitive && isAgent && v.status === 'IN_PROGRESS',
    answer: agentOrManager && v.status === 'IN_PROGRESS' && !!v.sensitive?.form,
    notes: agentOrManager,
    addPhoto: photosOpen && (v.sensitive?.photos.length ?? MAX_PHOTOS_PER_VISIT) < MAX_PHOTOS_PER_VISIT,
    removePhoto: agentOrManager && v.status !== 'CANCELLED' && (v.status !== 'DONE' || doneRecently),
  };
}
